import type { QueryClient } from "@tanstack/react-query";

/**
 * Lightweight in-memory store of recent React Query fetch durations, plus a
 * tracker that measures every query's round-trip (client → server action →
 * Postgres → back) by watching the QueryCache. This is the frontend half of
 * the scheduling-scope performance baseline: cross-reference a record's
 * `durationMs` here with the `[rpc] … Xms` server log (see
 * `utils/serverActions/_timing.ts`) — the gap is network + serialization.
 *
 * Read it from a component with `useSyncExternalStore(subscribeQueryPerf,
 * getQueryPerfSnapshot)`. Install the tracker once with
 * `installQueryPerfTracking(queryClient)`.
 */

export type QueryPerfRecord = {
  id: number;
  keyLabel: string;
  queryHash: string;
  durationMs: number;
  rowCount: number | null;
  status: "success" | "error";
  at: number;
};

const MAX_RECORDS = 100;

let records: QueryPerfRecord[] = [];
let seq = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function subscribeQueryPerf(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getQueryPerfSnapshot(): QueryPerfRecord[] {
  return records;
}

export function clearQueryPerf() {
  records = [];
  emit();
}

function labelForKey(queryKey: readonly unknown[]): string {
  return queryKey
    .slice(0, 3)
    .map((part) =>
      part === null || part === undefined
        ? "∅"
        : typeof part === "object"
          ? JSON.stringify(part)
          : String(part),
    )
    .join(" · ");
}

function countRows(data: unknown): number | null {
  if (Array.isArray(data)) return data.length;
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as { pages?: unknown }).pages)
  ) {
    const pages = (data as { pages: unknown[] }).pages;
    return pages.reduce<number>((total, page) => {
      if (Array.isArray(page)) return total + page.length;
      const rows = (page as { rows?: unknown })?.rows;
      return total + (Array.isArray(rows) ? rows.length : 0);
    }, 0);
  }
  return null;
}

/**
 * Subscribes to the query cache and records a timing whenever a query
 * transitions from `fetching` back to idle. Returns the unsubscribe fn.
 */
export function installQueryPerfTracking(queryClient: QueryClient): () => void {
  const cache = queryClient.getQueryCache();
  const starts = new Map<string, number>();

  return cache.subscribe((event) => {
    const query = event.query;
    const hash = query.queryHash;

    if (query.state.fetchStatus === "fetching") {
      if (!starts.has(hash)) starts.set(hash, performance.now());
      return;
    }

    const start = starts.get(hash);
    if (start == null) return;
    starts.delete(hash);

    const record: QueryPerfRecord = {
      id: ++seq,
      keyLabel: labelForKey(query.queryKey),
      queryHash: hash,
      durationMs: Math.round(performance.now() - start),
      rowCount: countRows(query.state.data),
      status: query.state.status === "error" ? "error" : "success",
      at: Date.now(),
    };
    records = [record, ...records].slice(0, MAX_RECORDS);
    emit();
  });
}
